/**
 * @file Cliente HTTP compartilhado do EP.
 * @module tools/ajax
 *
 * `ajax.get()` e `ajax.post()` aceitam exclusivamente chamadas por objeto. O
 * destino técnico pertence a `EPServiceTarget`; este arquivo documenta apenas
 * o envelope HTTP, resolução de origem, autenticação, retries e downloads.
 *
 * @see EPServiceTarget
 */

import Loader from "./loader";
import session from "./session";
import msgbox from "./msgbox";
import { isLocalAppEnv } from "@ep/tools/env";
import { buildServicePath, getServiceTargetError, normalizeServiceTarget } from "./serviceTarget.js";
import {
  getAuthOrigin,
  getDomainHost,
  getProductRootDomain,
  inferProductFromHost,
  isProductionHost,
  normalizeAppEnv,
} from "@ep/config/domainMap";

/**
 * Configuração de transporte de uma chamada Ajax.
 *
 * @typedef {Object} EPAjaxRequestConfig
 * @property {boolean|Object} [loader=true] `false` desabilita Loader; objeto configura uma instância.
 * @property {number} [retries=3] Quantidade máxima de tentativas para requests de leitura retryable.
 * @property {number} [retryDelay=500] Delay base em ms; cresce linearmente por tentativa.
 * @property {RequestCredentials|string} [credentials='include'] Política de credentials do Fetch.
 * @property {string} [product] Produto usado por `getParam()` quando necessário.
 */

/**
 * Envelope de chamada de serviço além do `EPServiceTarget`.
 *
 * @typedef {Object} EPAjaxServiceCall
 * @property {*} [body=null] Payload de POST. Valores escalares são encapsulados em `{data: value}`.
 * @property {Object<string,*>} [query] Query string usada por GET.
 * @property {Object<string,string>} [headers] Headers adicionais.
 * @property {EPAjaxRequestConfig} [config] Configuração de transporte.
 * @see EPServiceTarget
 */

/** Cada acesso direto `process.env.X` deve existir no DefinePlugin do produto (webpack injeta literais). */
const APP_HOST_ENV = process.env.APP_HOST;
const APP_ENV_ENV = process.env.APP_ENV;
const NODE_ENV_ENV = process.env.NODE_ENV;
const USE_HTTPS_ENV = process.env.USE_HTTPS;
const USE_SAME_ORIGIN_MASTER_RPC_ENV = process.env.USE_SAME_ORIGIN_MASTER_RPC;
const PRODUCT_NAME_ENV = process.env.PRODUCT_NAME;
const APP_PRODUCT_ENV = process.env.APP_PRODUCT;
const AUTH_PRODUCT_CODE_ENV = process.env.AUTH_PRODUCT_CODE;

/**
 * Reduz hostname para o domínio base usado pelos helpers legados de arquivo.
 *
 * @param {*} hostname Hostname.
 * @returns {string} Domínio normalizado.
 */
function normalizeBaseDomain(hostname) {
  const host = String(hostname || '').trim().toLowerCase();
  if (!host) return '';
  const parts = host.split('.');
  if (parts.length < 2) return host;
  if (parts.length > 2) {
    return parts.slice(1).join('.');
  }
  return host;
}

const lang = document.documentElement.lang;
let lastTransientProxyLogAt = 0;
const TRANSIENT_PROXY_LOG_COOLDOWN_MS = 15000;
let redirectingToLogin = false;
let showingRevokedSessionMessage = false;
const UNAUTH_REDIRECT_GRACE_KEY = 'ep:auth-401-grace-until';
const isSessionDataUrl = (url) =>
  /\/(?:(?:core\/core\/master\/getSessionData)|(?:service\/)?master\.getSessionData)(?:$|\?)/
    .test(String(url || ''));
const isMenuUrl = (url) =>
  /\/(?:(?:core\/core\/master\/getMenu)|(?:service\/)?master\.getMenu)(?:$|\?)/
    .test(String(url || ''));

/** Remove a janela de tolerância de redirect 401 da sessão do browser. @returns {void} */
function clearUnauthRedirectGrace() {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(UNAUTH_REDIRECT_GRACE_KEY);
  } catch {}
}

/**
 * Encerra a sessão local e informa revogação causada por novo login.
 *
 * A função protege contra múltiplas mensagens concorrentes e redireciona para
 * `/` após confirmação/fechamento.
 *
 * @returns {void}
 */
function notifySessionRevokedByNewLogin() {
  if (showingRevokedSessionMessage || typeof window === 'undefined') return;
  showingRevokedSessionMessage = true;
  redirectingToLogin = true;
  clearUnauthRedirectGrace();
  session.clear();

  /** Redireciona o browser para a raiz/login. @returns {void} */
  const redirect = () => window.location.replace('/');
  const title = window?.dic?.login?.sessionEndedByNewLoginTitle || 'Sessão encerrada';
  const message = window?.dic?.login?.sessionEndedByNewLoginMessage
    || 'Sua sessão foi encerrada porque esta conta entrou em outro dispositivo.';

  try {
    msgbox({
      type: 'warning',
      title,
      message,
      click: redirect,
      onClose: redirect,
    });
  } catch {
    alert(message);
    redirect();
  }
}

/**
 * Resolve ambiente efetivo da aplicação, protegendo hosts públicos contra env
 * local incorreto.
 *
 * @returns {string} Ambiente normalizado.
 */
function getAppEnv() {
  const normalized = normalizeAppEnv(APP_ENV_ENV || NODE_ENV_ENV || 'dev');
  if (typeof window === 'undefined') return normalized;

  if (isProductionHost(window.location?.hostname || '')) return 'prod';

  return normalized;
}

/**
 * Resolve o código do produto consumidor atual por env ou hostname.
 *
 * @returns {string} Código do produto, com fallback `platform`.
 */
function getCurrentProduct() {
  const explicit = String(
    PRODUCT_NAME_ENV
    || APP_PRODUCT_ENV
    || AUTH_PRODUCT_CODE_ENV
    || ''
  ).trim().toLowerCase();
  if (explicit) return explicit;

  return inferProductFromHost(
    APP_HOST_ENV
    || (typeof window !== 'undefined' ? window.location?.hostname : '')
  ) || 'platform';
}

/**
 * Resolve host raiz de um produto no ambiente atual.
 *
 * @param {string} product Produto.
 * @returns {string} Host mapeado/fallback.
 */
function getProductRootHost(product) {
  const normalized = String(product || '').trim().toLowerCase();
  const env = getAppEnv();
  const resolvedProduct = normalized || 'platform';
  const mappedHost = getProductRootDomain(resolvedProduct, env);
  if (mappedHost) {
    return mappedHost;
  }

  return normalizeBaseDomain(
    APP_HOST_ENV
    || (typeof window !== 'undefined' ? window.location?.hostname : '')
  );
}

/**
 * Normaliza nome de domínio técnico para comparação.
 *
 * @param {string} [domain=''] Domínio.
 * @returns {string} Domínio em minúsculas.
 */
function normalizeServiceDomain(domain = '') {
  const normalized = String(domain || '').trim().toLowerCase();
  return normalized;
}

/**
 * Decide se uma API pode ser chamada pela mesma origem, normalmente via proxy
 * local do produto.
 *
 * @param {string} [targetProduct=''] Produto alvo.
 * @returns {boolean} Estado de same-origin.
 */
function shouldUseSameOriginApi(targetProduct = '') {
  if (typeof window === 'undefined') return false;

  const hostname = String(window.location?.hostname || '').trim().toLowerCase();
  if (!hostname) return false;

  const useSameOriginByEnv = USE_SAME_ORIGIN_MASTER_RPC_ENV === true
    || USE_SAME_ORIGIN_MASTER_RPC_ENV === 'true'
    || USE_SAME_ORIGIN_MASTER_RPC_ENV === '1';
  const isLocalHost = hostname === 'localhost'
    || hostname === '127.0.0.1'
    || hostname.endsWith('.loc')
    || hostname.endsWith('.test');

  if (!isLocalHost && !useSameOriginByEnv) return false;

  const requestedProduct = String(targetProduct || '').trim().toLowerCase();
  if (!requestedProduct) return true;

  const currentProduct = getCurrentProduct();
  return requestedProduct === currentProduct;
}

/**
 * Cliente HTTP do EP.
 *
 * @class
 */
class Ajax {
  constructor() {
    // APP_HOST vem do DefinePlugin do webpack por produto (site vs platform).
    // getParam/getJSON usam este host; sem env em dev, mantém fallback do produto atual.
    const base = normalizeBaseDomain(
      APP_HOST_ENV
      || (typeof window !== 'undefined' ? window.location?.hostname : '')
    );
    const inferredProduct = inferProductFromHost(base) || 'platform';
    this.dev = base || getProductRootDomain(inferredProduct, 'dev');
    this.prod = base || getProductRootDomain(inferredProduct, 'prod');
  }

  /** @returns {'https'|'http'} Protocolo efetivo para URLs absolutas. */
  #getProtocol() {
    if (typeof window !== 'undefined' && window.location?.protocol === 'https:') {
      return 'https';
    }
    const useHttps = USE_HTTPS_ENV === true
      || USE_HTTPS_ENV === 'true'
      || USE_HTTPS_ENV === '1';
    return useHttps ? 'https' : 'http';
  }

  /**
   * Detecta objeto com chaves exclusivas de request config.
   *
   * @param {*} value Valor.
   * @returns {boolean} Estado.
   */
  #isRequestConfig(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    return ['loader', 'retries', 'retryDelay', 'credentials'].some((key) =>
      Object.prototype.hasOwnProperty.call(value, key)
    );
  }

  /**
   * Remove chaves de config de transporte que tenham sido colocadas em headers.
   *
   * @param {Object} [headers={}] Headers.
   * @returns {Object<string,*>} Headers limpos.
   */
  #normalizeHeaders(headers = {}) {
    if (!headers || typeof headers !== 'object' || Array.isArray(headers)) return {};
    const configOnlyKeys = new Set(['loader', 'retries', 'retryDelay', 'credentials']);
    return Object.fromEntries(
      Object.entries(headers).filter(([key, value]) =>
        !configOnlyKeys.has(key) && value !== undefined && value !== null
      )
    );
  }

  /**
   * Normaliza argumentos de `get/post` e rejeita a antiga assinatura posicional.
   *
   * @param {Array<*>} [args=[]] Argumentos recebidos.
   * @returns {Object} Chamada normalizada.
   * @throws {Error} Quando o primeiro argumento não é objeto.
   * @see EPServiceTarget
   */
  #normalizeCallArgs(args = []) {
    const [first] = args;
    const isObjectCall =
      first
      && typeof first === 'object'
      && !Array.isArray(first);

    if (!isObjectCall) {
      throw new Error('ajax.get/post requer objeto de chamada de serviço.');
    }

    const target = normalizeServiceTarget(first);
    return {
      ...target,
      body: first.body ?? null,
      query: first.query || {},
      headers: first.headers || {},
      config: first.config || {},
    };
  }

  /**
   * Valida o target sem lançar, registrando o erro no console.
   *
   * @param {EPServiceTarget} call Chamada.
   * @returns {boolean} Validade.
   */
  #validateServiceCall(call) {
    const error = getServiceTargetError(call);
    if (!error) return true;
    console.error(error);
    return false;
  }

  /**
   * Normaliza body de serviço. GET não altera body; POST escalar vira `{data}`.
   *
   * @param {EPAjaxServiceCall} call Chamada.
   * @param {string} method Método HTTP.
   * @returns {*} Body normalizado.
   */
  #buildServiceBody(call, method) {
    const body = call.body ?? null;
    const isGet = String(method || '').toUpperCase() === 'GET';
    if (isGet || body === null || body === undefined) return body;
    return body && typeof body === 'object' && !Array.isArray(body) ? body : { data: body };
  }

  /**
   * Anexa query string omitindo valores nulos/vazios.
   *
   * @param {string} url URL base.
   * @param {Object<string,*>} [params={}] Parâmetros.
   * @returns {string} URL resultante.
   */
  #appendQuery(url, params = {}) {
    const entries = Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '');
    if (entries.length === 0) return url;

    const qs = new URLSearchParams();
    entries.forEach(([key, value]) => qs.set(key, String(value)));
    return `${url}${String(url).includes('?') ? '&' : '?'}${qs.toString()}`;
  }

  /**
   * Monta URL de serviço e adiciona query apenas em GET.
   *
   * @param {Object} call Chamada.
   * @param {string} method Método HTTP.
   * @returns {Promise<string>} URL.
   */
  async #buildServiceUrl(call, method) {
    const url = await this.#getURL(call);
    if (String(method || '').toUpperCase() !== 'GET') return url;
    return this.#appendQuery(url, call.query);
  }

  /**
   * Resolve hostname da API de um produto.
   *
   * @param {string} product Produto alvo.
   * @returns {string} Host da API.
   */
  #resolveServiceHostname(product) {
    const resolvedProduct = String(product || getCurrentProduct()).trim().toLowerCase();
    return getDomainHost(resolvedProduct, 'api', getAppEnv());
  }

  /**
   * Executa GET de serviço pelo contrato canônico.
   *
   * @param {Object} call Chamada por objeto.
   * @returns {Promise<*>|undefined} Resposta; `undefined` quando validação falha.
   * @see EPServiceTarget
   */
  async get(...args) {
    const call = this.#normalizeCallArgs(args);
    if (!this.#validateServiceCall(call)) return;

    const url = await this.#buildServiceUrl(call, 'GET');
    const loaded = await this.load('GET', url, null, call.headers, call.config);

    return loaded;
  }

  /**
   * Carrega JSON público por URL absoluta ou por `/files/public/` do host atual.
   *
   * @param {string} url URL/caminho.
   * @returns {Promise<*>} Conteúdo.
   */
  async getJSON(url) {
    const source = String(url || '').trim();
    if (/^https?:\/\//i.test(source)) {
      return this.load('GET', source, null, {}, { loader: false });
    }

    const host = isLocalAppEnv() ? this.dev : this.prod;
    const protocol = this.#getProtocol();
    const resource = source.replace(/^\/+/, '');
    const path = `${protocol}://${host}/files/public/${resource}`;
    return this.load('GET', path, null, {}, { loader: false });
  }

  /**
   * Consulta parâmetro global pelo endpoint legado `master.getConfig`.
   *
   * @param {string} param Nome do parâmetro.
   * @param {EPAjaxRequestConfig} [config={}] Configuração.
   * @returns {Promise<*>} Resposta.
   */
  async getParam(param, config = {}) {
    const path = '/service/master.getConfig';
    const product = String(config?.product || getCurrentProduct() || 'platform').trim().toLowerCase();
    const directUrl = shouldUseSameOriginApi(product)
      ? path
      : `${this.#getProtocol()}://${this.#resolveServiceHostname(product)}${path}`;
    return await this.load('POST', directUrl, { param: param }, {}, { loader: false, ...config });
  }

  /**
   * Executa POST de serviço pelo contrato canônico.
   *
   * `category` continua aceito apenas porque `normalizeServiceTarget()` o trata
   * como alias legado de `service`; novas chamadas devem usar `service`.
   *
   * @param {Object} call Chamada por objeto.
   * @returns {Promise<*>|undefined} Resposta; `undefined` quando validação falha.
   *
   * @example
   * await ajax.post({
   *   scope: 'product',
   *   product: 'admindigital',
   *   service: 'crm',
   *   domain: 'contacts',
   *   module: 'people',
   *   method: 'save',
   *   body: { data: [] }
   * });
   *
   * @example
   * await ajax.post({
   *   scope: 'shared',
   *   service: 'reference',
   *   domain: 'geography',
   *   module: 'catalog',
   *   method: 'getDataCombo',
   *   body: { key: 'cb_countries' }
   * });
   *
   * @see EPServiceTarget
   */
  async post(...args) {
    const call = this.#normalizeCallArgs(args);
    if (!this.#validateServiceCall(call)) return;

    const url = await this.#buildServiceUrl(call, 'POST');
    return await this.load('POST', url, this.#buildServiceBody(call, 'POST'), call.headers, call.config);
  }

  /**
   * Resolve URL final de um target `shared`, `auth` ou produto comum.
   *
   * Targets shared são atendidos pela API do produto consumidor. Targets auth
   * usam a origem de autenticação fora de same-origin local.
   *
   * @param {EPServiceTarget} call Chamada.
   * @returns {Promise<string>} URL final.
   */
  async #getURL(call) {
    const target = normalizeServiceTarget(call);
    const path = buildServicePath(target);

    if (target.scope === 'shared') {
      const consumerProduct = getCurrentProduct();
      if (shouldUseSameOriginApi(consumerProduct)) {
        return path;
      }
      return `${this.#getProtocol()}://${this.#resolveServiceHostname(consumerProduct)}${path}`;
    }

    if (target.product === 'auth') {
      // Em ambiente local, o browser fala com o próprio app. O proxy do app
      // encaminha a chamada para a API e evita uma chamada cross-origin direta
      // para auth.app.loc (e, portanto, uma dependência de CORS no login).
      if (shouldUseSameOriginApi(getCurrentProduct())) {
        return path;
      }
      return `${getAuthOrigin(getAppEnv())}${path}`;
    }

    if (shouldUseSameOriginApi(target.product)) {
      return path;
    }

    const protocol = this.#getProtocol();
    const host = this.#resolveServiceHostname(target.product);
    return `${protocol}://${host}${path}`;
  }

  /**
   * Executa Fetch com autenticação, Loader, retry de leituras e suporte a
   * download por `Content-Disposition: attachment`.
   *
   * Regras de retry: GET sempre pode ser retryable; POST somente quando o código
   * reconhece a operação como leitura. Erros transitórios de proxy/rede usam
   * delay crescente. Requests de sessão revogada por novo login encerram a
   * sessão local apenas no endpoint explícito `master.getSessionData`.
   *
   * @param {string} method Método HTTP.
   * @param {string} url URL final.
   * @param {*} [body=null] Body já normalizado.
   * @param {Object<string,string>} [headers={}] Headers adicionais.
   * @param {EPAjaxRequestConfig} [config={}] Transporte.
   * @returns {Promise<*|boolean>} JSON, texto, `true` para download concluído.
   * @throws {Error} Último erro HTTP/rede após as tentativas permitidas.
   */
  async load(method, url, body = null, headers = {}, config = {}) {
    const showLoader = config.loader !== false;
    let loader = null;

    if (showLoader) {
      if (typeof config.loader === 'object') {
        loader = new Loader(config.loader);
      } else {
        loader = new Loader(); // padrão
      }
      loader.start();
    }

    const maxAttempts = Math.max(1, config.retries ?? 3);
    const retryDelay = config.retryDelay ?? 500;
    let lastError = null;
    const upperMethod = String(method || '').toUpperCase();
    const normalizedUrl = String(url || '');
    const isReadOnlyPost = upperMethod === 'POST' && (
      (typeof body?.method === 'string' && body.method.trim().toLowerCase().startsWith('get'))
      || /\/(?:service\/)?master\.getConfig(?:$|\?)/.test(normalizedUrl)
      // Endpoints hierárquicos de consulta, como `domains/getFormGroups`,
      // não carregam o método no body. Eles são seguros para nova tentativa.
      || /\/(?:get|list|search|lookup)[a-z0-9_]*(?:$|\?)/i.test(normalizedUrl)
    );

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const isGetMethod = upperMethod === 'GET';
      try {
        const isSessionDataRequest = isGetMethod && isSessionDataUrl(normalizedUrl);
        const isSessionPresentationRequest = isSessionDataRequest || (isGetMethod && isMenuUrl(normalizedUrl));
        const requestUrl = isSessionPresentationRequest
          ? `${String(url || '')}${String(url || '').includes('?') ? '&' : '?'}_ts=${Date.now()}`
          : url;
        const token = String(session.get('token') || '').trim();
        const requestProductCode = String(getCurrentProduct() || '').trim().toLowerCase();
        const hasValidToken = token && token !== 'null' && token !== 'undefined';
        const options = {
          method,
          credentials: config.credentials || 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(requestProductCode ? { 'X-Auth-Product-Code': requestProductCode } : {}),
            ...(hasValidToken ? { Authorization: `Bearer ${token}` } : {}),
            ...this.#normalizeHeaders(headers)
          }
        };

        if (isSessionPresentationRequest) {
          options.cache = 'no-store';
        }

        if (body && !isGetMethod && String(method || '').toUpperCase() !== 'HEAD') {
          options.body = JSON.stringify(body);
        }

        const response = await fetch(requestUrl, options);
        const disposition = response.headers.get('Content-Disposition');
        const contentType = response.headers.get('Content-Type') || '';

        if (disposition?.includes('attachment')) {
          const blob = await response.blob();
          const filename = disposition.match(/filename="?(.+?)"?$/)?.[1] || 'file.ad';

          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          a.download = filename;
          a.click();
          URL.revokeObjectURL(a.href);

          if (loader) loader.end();
          return true;
        }

        let data = null;
        let rawText = '';

        if (contentType.includes('application/json')) {
          try {
            data = await response.json();
          } catch {
            data = null;
          }
        } else {
          rawText = await response.text();
        }

        if (response.ok) {
          if (loader) loader.end();
          return data ?? rawText ?? {};
        }

        if (response.status === 401) {
          const errorCode = String(data?.error || data?.code || '').trim().toUpperCase();
          // Somente a consulta explícita da sessão tem autoridade para encerrar
          // o login. Um 401 de qualquer outro serviço é apenas erro daquele
          // serviço e deve ser devolvido ao chamador sem limpar estado local.
          if (isSessionDataRequest && errorCode === 'SESSION_REVOKED_BY_NEW_LOGIN') {
            notifySessionRevokedByNewLogin();
            if (loader) loader.end();
            const revokedError = new Error(errorCode);
            revokedError.status = response.status;
            revokedError.payload = data ?? rawText ?? null;
            throw revokedError;
          }
        }

        const serverMessage =
          data?.error
          || data?.message
          || data?.msg
          || rawText
          || `HTTP ${response.status} ${response.statusText || ''}`.trim();

        const requestError = new Error(serverMessage);
        requestError.status = response.status;
        requestError.payload = data ?? rawText ?? null;
        throw requestError;

      } catch (error) {
        lastError = error;
        const isNetworkError = error instanceof TypeError && error.message === 'Failed to fetch';
        const isLastAttempt = attempt === maxAttempts - 1;
        const isSessionDataRequest = isSessionDataUrl(normalizedUrl);
        const errorMessage = String(error?.message || '').toLowerCase();
        const status = Number(error?.status || 0);
        const isTransientProxyError =
          [502, 503, 504].includes(status)
          || errorMessage.includes('proxy falhou ao encaminhar')
          || errorMessage.includes('trying to proxy')
          || errorMessage.includes('bad gateway')
          || errorMessage.includes('econnreset')
          || errorMessage.includes('econnrefused')
          || errorMessage.includes('socket hang up');
        const isRetryableReadRequest = isGetMethod || isReadOnlyPost;
        const shouldRetry = (isNetworkError || isTransientProxyError) && isRetryableReadRequest;
        const isExpectedExpiredSession =
          isSessionDataRequest
          && (
            errorMessage.includes('sessão inválida ou expirada')
            || errorMessage.includes('sessao invalida ou expirada')
          );

        if (!shouldRetry || isLastAttempt) {
          if (loader) loader.end();
          if (!isExpectedExpiredSession) {
            if (isTransientProxyError) {
              const now = Date.now();
              if (now - lastTransientProxyLogAt >= TRANSIENT_PROXY_LOG_COOLDOWN_MS) {
                lastTransientProxyLogAt = now;
                console.error('Erro na requisição:', {
                  method,
                  url,
                  message: error?.message || String(error),
                });
              }
            } else {
              console.error('Erro na requisição:', {
                method,
                url,
                message: error?.message || String(error),
              });
            }
          }
          throw error;
        }

        // Retry com delay crescente
        await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
      }
    }

    if (loader) loader.end();
    throw lastError;
  }
}

const ajax = new Ajax();
export default ajax;
